import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Clock, AlertTriangle, Pill, CreditCard, FileText, Truck } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import apiClient from "../lib/api";

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("components");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  const lastNotifIdRef = useRef(null);
  const permissionAsked = useRef(false);

  useEffect(() => {
    if (!permissionAsked.current && "Notification" in window && Notification.permission === "default") {
      permissionAsked.current = true;
      setTimeout(() => Notification.requestPermission(), 3000);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 30000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const showBrowserNotification = (notif) => {
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        const n = new Notification(notif.title || "OMU", {
          body: notif.message || "",
          icon: "/logo192.png",
          badge: "/logo192.png",
          tag: notif.id,
          vibrate: [200, 100, 200]
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch (e) {
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(reg => {
            reg.showNotification(notif.title || "OMU", {
              body: notif.message || "",
              icon: "/logo192.png",
              badge: "/logo192.png",
              tag: notif.id,
              vibrate: [200, 100, 200]
            });
          });
        }
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get("/notifications?limit=10");
      const newNotifs = response.data.notifications || [];
      const newUnread = response.data.unread_count || 0;

      if (lastNotifIdRef.current && newNotifs.length > 0) {
        const latestId = newNotifs[0]?.id;
        if (latestId !== lastNotifIdRef.current && !newNotifs[0]?.read) {
          showBrowserNotification(newNotifs[0]);
        }
      }

      if (newNotifs.length > 0) {
        lastNotifIdRef.current = newNotifs[0]?.id;
      }

      setNotifications(newNotifs);
      setUnreadCount(newUnread);
    } catch (error) {
      console.log("Failed to fetch notifications");
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.put("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read");
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "subscription_expiring":
      case "subscription_expired":
        return <Clock className="h-4 w-4 text-amber-500" />;
      case "payment_validated":
        return <CreditCard className="h-4 w-4 text-green-500" />;
      case "payment_rejected":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "medicine_available":
        return <Pill className="h-4 w-4 text-blue-500" />;
      case "prescription_pending":
      case "prescription_on_hold":
      case "prescription_validated":
      case "prescription_rejected":
      case "prescription_validated_pharmacy":
      case "prescription_rejected_pharmacy":
      case "purchase_awaiting_cnom":
      case "purchase_request":
        return <FileText className="h-4 w-4 text-omu-blue-500" />;
      case "delivery_job":
      case "delivery_picked_up":
      case "delivery_delivered":
      case "purchase_paid_delivery":
        return <Truck className="h-4 w-4 text-omu-blue-500" />;
      case "doctor_approved":
      case "doctor_pending":
      case "doctor_rejected":
        return <FileText className="h-4 w-4 text-omu-blue-500" />;
      default:
        return <Bell className="h-4 w-4 text-slate-500" />;
    }
  };

  const handleNotificationClick = (notification) => {
    const type = notification.type || "";
    if (user?.role === "admin") {
      if (type === "delivery_job" || type.startsWith("delivery_")) {
        navigate("/admin/deliveries");
        return;
      }
      if (type.includes("doctor") || notification.data?.entity_type === "doctor") {
        navigate("/admin/doctors?status=pending");
        return;
      }
      if (type.includes("payment") || notification.data?.purpose === "purchase" || notification.data?.payment_id) {
        navigate("/admin/payments");
        return;
      }
    }
    if (type.startsWith("prescription") || type === "purchase_awaiting_cnom" || type === "purchase_request") {
      if (user?.role === "doctor") navigate("/doctor/prescriptions");
      else if (type === "prescription_on_hold") navigate("/messages");
      else navigate("/purchases");
      return;
    }
    if (type === "delivery_job" || type.startsWith("delivery_")) {
      if (user?.role === "delivery") navigate("/delivery");
      else navigate("/purchases");
      return;
    }
    if (type === "purchase_paid_delivery" || type.startsWith("purchase_payment")) {
      navigate("/purchases");
    }
  };

  const locale = i18n.language === "en" ? "en-US" : "fr-FR";

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("timeJustNow");
    if (diffMins < 60) return t("timeMinutesAgo", { count: diffMins });
    if (diffHours < 24) return t("timeHoursAgo", { count: diffHours });
    if (diffDays < 7) return t("timeDaysAgo", { count: diffDays });
    return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-slate-900">{t("notificationsTitle")}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-omu-blue-600 hover:text-omu-blue-700"
              onClick={markAllAsRead}
            >
              {t("markAllRead")}
            </Button>
          )}
        </div>

        <ScrollArea className="h-[350px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">{t("noNotifications")}</p>
            </div>
          ) : (
            <div className="py-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                    !notification.read ? "bg-blue-50/50" : ""
                  }`}
                  onClick={() => {
                    if (!notification.read) markAsRead(notification.id);
                    handleNotificationClick(notification);
                    setOpen(false);
                  }}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? "font-semibold" : "font-medium"} text-slate-900 line-clamp-1`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-omu-blue-500 flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
